import {
  ClockInput,
  AmountField,
  aprons,
  BinIcon,
  ChevronIcon,
  carriedKinds,
  DatePick,
  getAircraft,
  getAirlines,
  getAirports,
  getRegistration,
  findReference,
  lookupFlight,
  MoreIcon,
  NumberField,
  Pick,
  PlusIcon,
  Popover,
  Suggest,
  SyncIcon,
  type Suggestion,
  type Aircraft,
  type Airline,
  type Apron,
  type Booking,
  type Carried,
  type KnownAirport,
  type FoundItem,
  type LookedUpFlight,
  type Mode,
  type Money,
  type PassengerBags,
  type Point,
  type SeatKind,
  type Spot,
  type TicketInput,
  type TicketRules,
} from '@trippath/shared';
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { timeOf, wallOf } from '../../lib/clock';
import { FoundSuggest } from './FoundSuggest';
import { ApronIcon } from '../way/LegEnds';
import { SeatKindPick, SeatMapIcon } from './SeatKindPick';
import { SeatMap } from './SeatMap';
import { placeOf } from '../../lib/seats';
import { useSeatPlans } from './useSeatPlans';
import {
  aircraftLabel,
  airlineCode,
  airlineLabel,
  airlineOfFlight,
  airportLabel,
  findAircraft,
  findAirlines,
  findAirports,
  typeCodeOf,
} from '../../lib/reference';

/** A registration as typed, `VH-YQR`, `A6APG`, `N123AB`: letters and digits, maybe a dash. */
const REGISTRATION = /^[A-Z0-9]{1,3}-?[A-Z0-9]{2,6}$/i;

/** How long after the last key a registration is looked up. */
const LOOKUP_MS = 400;

/** A flight number that reads as one, `EY 451` as `EY451`; `null` for anything else. */
function flightNumberOf(typed: string): string | null {
  const plain = typed.replace(/\s/g, '').toUpperCase();
  return /^[A-Z0-9]{2}\d{1,4}[A-Z]?$/.test(plain) && /[A-Z]/.test(plain.slice(0, 2))
    ? plain
    : null;
}

/** How a flight data service is named. */
function serviceLabel(name: string): string {
  return (
    { aerodatabox: 'AeroDataBox', airlabs: 'AirLabs', aviationstack: 'Aviationstack' }[name] ??
    name
  );
}

/** A leg with what a flight looked up says, where the leg says nothing yet. */
export function filled(
  leg: LegDraft,
  found: LookedUpFlight,
  airlines: readonly Airline[],
): LegDraft {
  const or = (typed: string, said: string | null) =>
    typed.trim() === '' && said ? said : typed;
  const clock = (at: string | null) => at?.slice(11, 16) ?? null;
  const day = (at: string | null) => at?.slice(0, 10) ?? null;
  const airline = found.airline ? airlines.find((a) => a.code === found.airline) : undefined;
  return {
    ...leg,
    origin: or(leg.origin, found.origin),
    destination: or(leg.destination, found.destination),
    departureTime: or(leg.departureTime, clock(found.departure)),
    arrivalDay:
      leg.arrivalTime.trim() === '' ? (day(found.arrival) ?? leg.arrivalDay) : leg.arrivalDay,
    arrivalTime: or(leg.arrivalTime, clock(found.arrival)),
    actualDeparture: or(leg.actualDeparture, clock(found.actualDeparture)),
    actualArrival: or(leg.actualArrival, clock(found.actualArrival)),
    airline: or(leg.airline, airline ? airlineLabel(airline) : found.airline),
    vehicle: or(leg.vehicle, found.aircraft),
    registration: or(leg.registration, found.registration),
    gate: or(leg.gate, found.gate),
    arrivalGate: or(leg.arrivalGate, found.arrivalGate),
    terminalFrom: or(leg.terminalFrom, found.terminalFrom),
    terminalTo: or(leg.terminalTo, found.terminalTo),
  };
}

/** A leg as typed: its places, its wall clocks at each end, its numbers. */
export interface LegDraft {
  origin: string;
  destination: string;
  /** The day and the clock it leaves, local where it leaves. */
  departureDay: string | null;
  departureTime: string;
  arrivalDay: string | null;
  arrivalTime: string;
  actualDeparture: string;
  actualArrival: string;
  number: string;
  /** The airline as typed or chosen: `Finnair (AY)`. */
  airline: string;
  /** Who runs it when no airline does: `Elron`, `Tallink`. */
  operator: string;
  /** The aircraft, the train's type, the bus's model, the ship, the car. */
  vehicle: string;
  registration: string;
  travelClass: string;
  /** A vehicle carried aboard a ferry, and its plate. */
  carried: Carried | null;
  carriedPlate: string;
  gate: string;
  arrivalGate: string;
  /** How it is boarded and left; `null` while not said. */
  boarding: Apron | null;
  deplaning: Apron | null;
  terminalFrom: string;
  terminalTo: string;
}

/** A passenger as typed. */
export interface PassengerDraft {
  name: string;
  traveller: string | null;
  price: number | null;
  number: string;
  seats: string[];
  /** A coach on each leg, by train. */
  coaches: string[];
  /** Where the seat is on each leg, as picked; none where not. */
  kinds: (SeatKind | null)[];
  /** Their own bags; `null` for the ticket's. */
  bags: PassengerBags | null;
}

/** A ticket's legs and people, as typed. */
export interface TicketDraft {
  legs: LegDraft[];
  passengers: PassengerDraft[];
  rules: TicketRules;
}

/** A ticket that says nothing of what it allows. */
export const NO_RULES: TicketRules = {
  cabinPieces: null,
  cabinKg: null,
  cabinSize: null,
  personalItem: false,
  checkedBags: [],
  onlineOpensH: null,
  onlineClosesM: null,
  deskOpensH: null,
  deskClosesM: null,
  notes: '',
};

/** The zones a ticket's ends are in when no airport says: where it leaves, where it arrives. */
export interface TicketZones {
  from: string | null;
  to: string | null;
}

/** Where a leg not yet said goes from and to, and the day it leaves. */
export interface LegStart {
  origin: string;
  destination: string;
  day: string | null;
  /** The day it arrives, when known; else the day it leaves. */
  arrival?: string | null;
  /** Its clock at each end, `16:50`, as the way says; `''` when not said. */
  departureTime?: string;
  arrivalTime?: string;
  /** Its flight or train number, as the ticket was read with it. */
  number?: string;
  /** Who runs it, its ship, and where it leaves from, as a ticket's file says. */
  operator?: string;
  vehicle?: string;
  /** A flight's airline, as the form names one: `Air France (AF/AFR)`. */
  airline?: string;
  gate?: string;
}

/**
 * A ticket's details as the booking has them, its times local at each end; a ticket of no legs
 * yet a leg to each stop it goes through, its changes on the way.
 */
export function draftOf(
  booking: Booking | undefined,
  starts: LegStart[],
  zones: TicketZones,
): TicketDraft {
  const legs = booking?.legs ?? [];
  const wall = (at: string | null, zone: string | null) =>
    at === null ? null : wallOf(at, zone);
  const [first] = starts;
  return {
    legs:
      legs.length === 0
        ? starts.map((start) => blankLeg(start))
        : withEnds(
            legs.map((leg) => {
              const from = leg.originZone ?? zones.from;
              const to = leg.destinationZone ?? zones.to;
              const departs = wall(leg.departureAt, from);
              const arrives = wall(leg.arrivalAt, to);
              return {
                origin: leg.origin,
                destination: leg.destination,
                departureDay: departs?.day ?? null,
                departureTime: departs?.time ?? '',
                arrivalDay: arrives?.day ?? null,
                arrivalTime: arrives?.time ?? '',
                actualDeparture: wall(leg.actualDepartureAt, from)?.time ?? '',
                actualArrival: wall(leg.actualArrivalAt, to)?.time ?? '',
                number: leg.number ?? '',
                airline:
                  leg.airline === null
                    ? ''
                    : leg.airlineName === null
                      ? leg.airline
                      : `${leg.airlineName} (${leg.airline})`,
                operator: leg.operator ?? '',
                vehicle: leg.vehicle ?? '',
                registration: leg.registration ?? '',
                travelClass: leg.travelClass ?? '',
                carried: leg.carried,
                carriedPlate: leg.carriedPlate ?? '',
                gate: leg.gate ?? '',
                arrivalGate: leg.arrivalGate ?? '',
                boarding: leg.boarding,
                deplaning: leg.deplaning,
                terminalFrom: leg.terminalFrom ?? '',
                terminalTo: leg.terminalTo ?? '',
              };
            }),
            first,
          ),
    passengers: (booking?.passengers ?? []).map((p) => ({
      name: p.name,
      traveller: p.traveller,
      price: p.price?.minor ?? null,
      number: p.number ?? '',
      seats: p.seats.map((seat) => seat ?? ''),
      coaches: p.coaches.map((coach) => coach ?? ''),
      kinds: p.kinds,
      bags: p.bags,
    })),
    rules: booking?.rules ?? NO_RULES,
  };
}

/**
 * A ticket's flights with what the ticket says filling what they do not: the first leaves the day
 * and at the time the ticket says, and arrives when its way does.
 */
export function withEnds(legs: LegDraft[], ends: LegStart | undefined): LegDraft[] {
  return legs.map((leg, at) =>
    at === 0 && ends
      ? {
          ...leg,
          departureDay: leg.departureDay ?? ends.day,
          departureTime: leg.departureTime || (ends.departureTime ?? ''),
          arrivalTime: leg.arrivalTime || (ends.arrivalTime ?? ''),
        }
      : leg,
  );
}

function blankLeg({
  origin,
  destination,
  day,
  arrival = day,
  departureTime = '',
  arrivalTime = '',
  number = '',
  operator = '',
  vehicle = '',
  airline = '',
  gate = '',
}: LegStart): LegDraft {
  return {
    origin,
    destination,
    departureDay: day,
    departureTime,
    arrivalDay: arrival,
    arrivalTime,
    actualDeparture: '',
    actualArrival: '',
    number,
    airline,
    operator,
    vehicle,
    registration: '',
    travelClass: '',
    carried: null,
    carriedPlate: '',
    gate,
    arrivalGate: '',
    boarding: null,
    deplaning: null,
    terminalFrom: '',
    terminalTo: '',
  };
}

/**
 * A leg changed: its day moved, the day it arrives moved as far with it, unless said too — a bus
 * a night later still lands the day it leaves.
 */
export function changed(l: LegDraft, change: Partial<LegDraft>): LegDraft {
  const from = l.departureDay;
  const to = change.departureDay;
  const arrival = l.arrivalDay;
  if (change.arrivalDay !== undefined || !from || !to || !arrival) return { ...l, ...change };
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
  const moved = new Date(Date.parse(arrival) + days * 86_400_000).toISOString().slice(0, 10);
  return { ...l, ...change, arrivalDay: moved };
}

/**
 * A ticket's details as sent: each time a wall clock at its place, `2026-10-28T19:00` — the
 * server knows each airport's zone; the first and last ends say theirs when no airport does —
 * or `undefined` when a time typed does not read.
 */
export function inputOf(
  draft: TicketDraft,
  zones: TicketZones,
  airlines: readonly Airline[] = [],
): TicketInput | undefined {
  const clocks = draft.legs.flatMap((l) => [
    l.departureTime,
    l.arrivalTime,
    l.actualDeparture,
    l.actualArrival,
  ]);
  if (clocks.some((clock) => timeOf(clock) === undefined)) return undefined;
  const at = (day: string | null, time: string) => {
    const clock = timeOf(time);
    return day === null || typeof clock !== 'string' ? null : `${day}T${clock}`;
  };
  const last = draft.legs.length - 1;
  const said = (typed: string) => (typed.trim() === '' ? null : typed);
  // Each leg's seat or coach, none when not said.
  const perLeg = (typed: string[]) => draft.legs.map((_, index) => said(typed[index] ?? ''));
  const legs = draft.legs.map((leg, index) => ({
    origin: leg.origin.trim(),
    destination: leg.destination.trim(),
    departureAt: at(leg.departureDay, leg.departureTime),
    // Its day not said, the day it leaves: the server takes it on to the first after it leaves.
    arrivalAt: at(leg.arrivalDay ?? leg.departureDay, leg.arrivalTime),
    actualDepartureAt: at(leg.departureDay, leg.actualDeparture),
    actualArrivalAt: at(leg.arrivalDay ?? leg.departureDay, leg.actualArrival),
    number: leg.number.trim() === '' ? null : leg.number,
    airline: airlineCode(leg.airline, airlines),
    operator: said(leg.operator),
    vehicle: said(leg.vehicle),
    registration: said(leg.registration),
    travelClass: said(leg.travelClass),
    carried: leg.carried,
    carriedPlate: leg.carried === null ? null : said(leg.carriedPlate),
    gate: leg.gate.trim() === '' ? null : leg.gate,
    arrivalGate: leg.arrivalGate.trim() === '' ? null : leg.arrivalGate,
    boarding: leg.boarding,
    deplaning: leg.deplaning,
    terminalFrom: leg.terminalFrom.trim() === '' ? null : leg.terminalFrom,
    terminalTo: leg.terminalTo.trim() === '' ? null : leg.terminalTo,
    originZone: index === 0 ? zones.from : null,
    destinationZone: index === last ? zones.to : null,
  }));
  const passengers = draft.passengers.map((p) => ({
    name: p.name,
    traveller: p.traveller,
    price: p.price,
    number: p.number.trim() === '' ? null : p.number,
    seats: perLeg(p.seats),
    coaches: perLeg(p.coaches),
    kinds: draft.legs.map((_, index) => p.kinds[index] ?? null),
    bags: p.bags,
  }));
  return { legs, passengers, rules: draft.rules };
}

interface Props {
  draft: TicketDraft;
  onChange: (draft: TicketDraft) => void;
  /** The ticket's price, shared among its people unless each says theirs. */
  price: number | null;
  currency: string;
  /** The fellow travellers, to put one on the ticket by name. */
  travellers: { id: string; name: string }[];
  /** The trip's own people by name: its owner and those invited who joined. */
  members?: string[];
  /** Which part shows: its legs, its people, or what it allows. */
  part: 'legs' | 'people' | 'rules';
  /** How it goes: by air its legs are flights, with an airline and an aircraft. */
  mode: Mode | null;
  /** The leg it was opened for, by its ends: only it shown until all are asked for. */
  sector?: Sector | null;
  /** Where the way leaves and arrives, its towns: the stations near them offered and mapped. */
  towns?: { from: Point | null; to: Point | null };
  /** The stations it is said to leave from and arrive at, pinned on the map. */
  stations?: { from: Spot | null; to: Spot | null };
  /** A station picked where it is, at the ticket's first start or its last end. */
  onEnd?: (side: 'from' | 'to', spot: Spot) => void;
}

/** A leg by its ends, as its codes say them: `SYD` → `AUH`. */
export interface Sector {
  origin: string;
  destination: string;
}

/** The airlines and aircraft types known, asked for once a ticket by air is open. */
interface Reference {
  airports: KnownAirport[];
  airlines: Airline[];
  aircraft: Aircraft[];
}

export function useReference(asked: boolean): Reference {
  const [known, setKnown] = useState<Reference>({ airports: [], airlines: [], aircraft: [] });
  useEffect(() => {
    if (!asked) return;
    Promise.all([getAirports(), getAirlines(), getAircraft()]).then(
      ([airports, airlines, aircraft]) => {
        setKnown({ airports, airlines, aircraft });
      },
      // Without them the fields are typed as they are.
      () => undefined,
    );
  }, [asked]);
  return known;
}

/** The lists a leg's operator, vehicle and places are suggested from, by how it goes. */
const SUGGESTED: Record<
  Exclude<Mode, 'flight'>,
  { operator: string; vehicle?: string; place?: string }
> = {
  train: { operator: 'rail_operators,transit', vehicle: 'train_types', place: 'stations' },
  bus: { operator: 'bus_operators,transit', place: 'stations' },
  ferry: { operator: 'shipping_lines', vehicle: 'ships' },
  car: { operator: 'car_rentals', vehicle: 'car_models' },
  // A trail has no ticket's operator: a guide's company, if any, is typed.
  foot: { operator: 'transit' },
};

/** An IMO number as typed: seven digits. */
const IMO = /^\d{7}$/;

/** A leg on a ship known: its name, IMO number and operator, where the leg says none yet. */
function aboard(leg: LegDraft, ship: FoundItem): Partial<LegDraft> {
  return {
    vehicle: ship.name,
    registration: ship.code,
    operator: leg.operator.trim() === '' ? ship.detail : leg.operator,
  };
}

/**
 * The trip's people, those whose names fit what is typed first — every one of them offered, as a
 * name kept on the ticket may be none of theirs — then the name typed as one of its own when it
 * is none of them.
 */
function peopleFor(
  typed: string,
  members: string[],
  travellers: { name: string }[],
  t: (key: string, options: Record<string, string>) => string,
): Suggestion[] {
  const wanted = typed.trim().toLowerCase();
  const names = [...new Set([...members, ...travellers.map((one) => one.name)])];
  const fit = names.toSorted(
    (a, b) =>
      Number(b.toLowerCase().includes(wanted)) - Number(a.toLowerCase().includes(wanted)),
  );
  const own =
    wanted !== '' && !names.some((name) => name.toLowerCase() === wanted)
      ? [{ value: typed.trim(), label: t('ticket.newPerson', { name: typed.trim() }) }]
      : [];
  return [...fit.map((name) => ({ value: name, label: name })), ...own];
}

/**
 * Looks a flight up once it has its number and its day, by the arrows in its number's field;
 * till then they say, when pointed at, what they wait for.
 */
function Lookup({
  day,
  number,
  onLook,
}: {
  day: string | null;
  number: string;
  onLook: (day: string) => void;
}) {
  const { t } = useTranslation();
  // The day to look it up on, once it has a number too.
  const on = flightNumberOf(number) === null ? null : day;
  const said = t(on === null ? 'ticket.lookupNeeds' : 'ticket.lookup');
  const look = { className: 'icon-button icon-button-small field-action', title: said };
  return on === null ? (
    <button type="button" {...look} aria-label={said} disabled>
      <SyncIcon />
    </button>
  ) : (
    <button
      type="button"
      {...look}
      aria-label={said}
      onClick={() => {
        onLook(on);
      }}
    >
      <SyncIcon />
    </button>
  );
}

/** A part's menu, at the right of the ticket's parts: one more added — a leg, a bag. */
export function PartMenu({
  label,
  add,
  onAdd,
}: {
  label: string;
  add: string;
  onAdd: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="part-menu">
      <Popover
        open={open}
        onOpenChange={setOpen}
        align="end"
        label={label}
        trigger={(props) => (
          <button
            {...props}
            type="button"
            className="icon-button icon-button-small"
            aria-label={label}
            title={label}
          >
            <MoreIcon />
          </button>
        )}
      >
        <div className="data-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="data-menu-item"
            onClick={() => {
              setOpen(false);
              onAdd();
            }}
          >
            {add}
          </button>
        </div>
      </Popover>
    </div>
  );
}

/** A ticket with one leg more, on from where its last arrives, the day it does. */
export function withLeg(draft: TicketDraft): TicketDraft {
  const before = draft.legs.at(-1);
  return {
    ...draft,
    legs: [
      ...draft.legs,
      blankLeg({
        origin: before?.destination ?? '',
        destination: '',
        day: before?.arrivalDay ?? null,
      }),
    ],
  };
}

/** A checked bag more for one person, as heavy as their last. */
function AddBag({
  bags,
  onChange,
}: {
  bags: PassengerBags;
  onChange: (bags: PassengerBags) => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="icon-button icon-button-small"
      aria-label={t('ticket.addBag')}
      title={t('ticket.addBag')}
      onClick={() => {
        onChange({ ...bags, checked: [...bags.checked, bags.checked.at(-1) ?? 23] });
      }}
    >
      <PlusIcon />
    </button>
  );
}

/** One person's own bags: their cabin bag's kilograms, each checked bag's. */
function OwnBags({
  bags,
  list,
  onChange,
}: {
  bags: PassengerBags;
  /** The checked bags as the ticket's are said. */
  list: (bags: number[], set: (bags: number[]) => void) => ReactNode;
  onChange: (bags: PassengerBags) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="ticket-own-bags">
      <div className="form-row ticket-numbers">
        <NumberField
          label={t('ticket.cabinKg')}
          value={bags.cabinKg}
          min={0}
          max={99}
          onChange={(cabinKg) => {
            onChange({ ...bags, cabinKg });
          }}
        />
      </div>
      {list(bags.checked, (checked) => {
        onChange({ ...bags, checked });
      })}
    </div>
  );
}

/** A ticket's legs — from where to where, when, its number and aircraft — and its people. */
export function TicketEditor({
  draft,
  onChange,
  price,
  currency,
  travellers,
  members = [],
  part,
  mode,
  sector = null,
  towns = { from: null, to: null },
  stations = { from: null, to: null },
  onEnd,
}: Props) {
  const { t } = useTranslation();
  const ids = useId();

  // The legs folded to their heading, each by its own; opened for one leg, the others folded.
  const [folded, setFolded] = useState<number[]>(() => {
    const other = draft.legs.flatMap((l, at) =>
      sector !== null && (l.origin !== sector.origin || l.destination !== sector.destination)
        ? [at]
        : [],
    );
    return other.length < draft.legs.length ? other : [];
  });
  const [person, setPerson] = useState(0);
  // The leg whose seat map shows under the seats, if one.
  const [seatMapOf, setSeatMapOf] = useState<number | null>(null);
  const flight = mode === 'flight';
  const { airports, airlines, aircraft } = useReference(flight);
  // None for a flight: its airline and aircraft are its own lists.
  const suggested = mode === null || mode === 'flight' ? undefined : SUGGESTED[mode];
  const mapIndex = seatMapOf ?? -1;
  const seatMapLeg = draft.legs[mapIndex];
  const seatMapType = seatMapLeg ? typeCodeOf(seatMapLeg.vehicle, aircraft) : null;
  // What a leg's fields are called by how it goes: a platform at a station, a berth at a port.
  const kind = mode ?? 'none';
  const by = (key: string, options: Record<string, string> = {}) =>
    t(`ticket.${key}.${kind}`, options);
  const train = mode === 'train';
  const ferry = mode === 'ferry';
  const car = mode === 'car';
  // Airports and ports have terminals; stations, stops and a car hire do not.
  const terminals = mode === null || flight || ferry;
  const classes =
    mode === 'train'
      ? (['first', 'second', 'sleeper', 'couchette'] as const)
      : mode === 'ferry'
        ? (['deck', 'inside', 'outside', 'suite'] as const)
        : [];
  // The ticket as it is now, and where it goes, for an answer that comes after it changed.
  const latest = useRef({ draft, onChange });
  useEffect(() => {
    latest.current = { draft, onChange };
  }, [draft, onChange]);
  // A registration typed says its aircraft, when none is said yet: VH-YQR is a Boeing 737-800;
  // asked once typing stops.
  const [ask, setAsk] = useState<{ index: number; registration: string } | null>(null);
  useEffect(() => {
    if (ask === null) return;
    const timer = window.setTimeout(() => {
      getRegistration(ask.registration).then(
        (found) => {
          const now = latest.current;
          if (!found || now.draft.legs[ask.index]?.vehicle.trim() !== '') return;
          now.onChange({
            ...now.draft,
            legs: now.draft.legs.map((l, at) =>
              at === ask.index ? { ...l, vehicle: found.aircraft ?? found.description } : l,
            ),
          });
        },
        // Not found or not asked: the aircraft is typed.
        () => undefined,
      );
    }, LOOKUP_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [ask]);
  // A flight looked up by its number and day: what it says fills what is not said yet.
  const [lookedUp, setLookedUp] = useState<Record<number, string>>({});
  const fill = (index: number, there: LegDraft, day: string) => {
    setLookedUp((was) => ({ ...was, [index]: t('ticket.lookingUp') }));
    lookupFlight(there.number, day).then(
      ({ found, tried }) => {
        setLookedUp((was) => ({
          ...was,
          [index]: found
            ? t('ticket.lookupFound', { service: serviceLabel(found.service) })
            : tried.length === 0
              ? t('ticket.lookupNoService')
              : t('ticket.lookupNone'),
        }));
        if (!found) return;
        const now = latest.current;
        now.onChange({
          ...now.draft,
          legs: now.draft.legs.map((l, at) => (at === index ? filled(l, found, airlines) : l)),
        });
      },
      () => {
        setLookedUp((was) => ({ ...was, [index]: t('ticket.lookupFailed') }));
      },
    );
  };
  // An IMO number typed says its ship, once the server has found it.
  const [imo, setImo] = useState<{ index: number; code: string } | null>(null);
  useEffect(() => {
    if (imo === null) return;
    findReference('ships', imo.code).then(
      (found) => {
        const ship = found.find((one) => one.code === imo.code);
        if (!ship) return;
        const now = latest.current;
        now.onChange({
          ...now.draft,
          legs: now.draft.legs.map((l, at) =>
            at === imo.index ? { ...l, ...aboard(l, ship) } : l,
          ),
        });
      },
      // Not found: the ship is typed.
      () => undefined,
    );
  }, [imo]);
  const lookUp = (index: number, registration: string) => {
    if (REGISTRATION.test(registration.trim())) setAsk({ index, registration });
  };
  const leg = (index: number, change: Partial<LegDraft>) => {
    onChange({
      ...draft,
      legs: draft.legs.map((l, at) => (at === index ? changed(l, change) : l)),
    });
  };
  const passenger = (index: number, change: Partial<PassengerDraft>) => {
    onChange({
      ...draft,
      passengers: draft.passengers.map((p, at) => (at === index ? { ...p, ...change } : p)),
    });
  };
  const field = (
    id: string,
    label: string,
    value: string,
    set: (value: string) => void,
    hint = '',
    action: ReactNode = null,
    beside: ReactNode = null,
    // What a text pressed in the PDF gives it: a clock's field its clock alone, a code's its code.
    takes: 'clock' | 'code' | null = null,
  ) => (
    <div className="field">
      <label className="field-label" htmlFor={`${ids}-${id}`}>
        {label}
      </label>
      <div
        className={
          action !== null ? 'field-with-action' : beside !== null ? 'field-beside' : undefined
        }
      >
        {takes === 'clock' ? (
          <ClockInput
            id={`${ids}-${id}`}
            placeholder={hint}
            data-takes-text="clock"
            value={value}
            onChange={set}
          />
        ) : (
          <input
            id={`${ids}-${id}`}
            className="text-input"
            type="text"
            autoComplete="off"
            placeholder={hint}
            data-takes-text={takes ?? undefined}
            value={value}
            onChange={(event) => {
              set(event.target.value);
            }}
          />
        )}
        {action}
        {beside}
      </div>
    </div>
  );
  // Checked bags, each by its kilograms, each to take off.
  const bagList = (bags: number[], set: (bags: number[]) => void) =>
    bags.length > 0 && (
      <div className="form-row ticket-numbers">
        {bags.map((kg, at) => (
          <div key={at} className="ticket-bag">
            <NumberField
              label={t('ticket.bag', { number: at + 1 })}
              value={kg}
              min={0}
              max={999}
              onChange={(n) => {
                set(bags.map((was, i) => (i === at ? (n ?? 0) : was)));
              }}
            />
            <button
              type="button"
              className="icon-button icon-button-small icon-button-danger"
              aria-label={t('ticket.removeBag', { number: at + 1 })}
              title={t('ticket.removeBag', { number: at + 1 })}
              onClick={() => {
                set(bags.filter((_, i) => i !== at));
              }}
            >
              <BinIcon />
            </button>
          </div>
        ))}
      </div>
    );
  // A station or a stop: by train or bus, Europe's stations suggested; OpenStreetMap's near the
  // town too, or one pinned on the map — where the ticket starts or ends, the way's end with it.
  const place = (
    value: string,
    set: (value: string) => void,
    near: Point | null,
    end: 'from' | 'to' | null,
  ) => (
    <FoundSuggest
      label={by('placeBy')}
      value={value}
      lists={suggested?.place}
      near={near}
      picked={end === null ? null : stations[end]}
      onChange={set}
      onPick={(one) => {
        set(one.name);
      }}
      onSpot={(spot) => {
        set(spot.name);
        if (end !== null) onEnd?.(end, spot);
      }}
    />
  );
  // How a flight is boarded or left: one of three, pressed again to say none.
  const apron = (
    id: string,
    label: string,
    value: Apron | null,
    set: (way: Apron | null) => void,
  ) => (
    <div className="field">
      <span className="field-label" id={`${ids}-${id}`}>
        {label}
      </span>
      <div className="segmented apron-pick" role="group" aria-labelledby={`${ids}-${id}`}>
        {aprons.map((way) => (
          <button
            key={way}
            type="button"
            aria-pressed={value === way}
            onClick={() => {
              set(value === way ? null : way);
            }}
          >
            <ApronIcon apron={way} />
            {t(`ticket.apron.${way}`)}
          </button>
        ))}
      </div>
    </div>
  );
  // Each leg's seat map, as its aircraft and airline say: a seat placed by it.
  const flown = draft.legs.map((l) => ({
    vehicle: typeCodeOf(l.vehicle, aircraft) ?? l.vehicle,
    airline: airlineCode(l.airline, airlines),
  }));
  const seatPlan = useSeatPlans(flown);
  // Where a seat typed is on the aircraft, once its map has come.
  const placeFor = (l: LegDraft, seat: string) => {
    const plan = seatPlan({
      vehicle: typeCodeOf(l.vehicle, aircraft) ?? l.vehicle,
      airline: airlineCode(l.airline, airlines),
    });
    return plan && seat.trim() !== '' ? placeOf(plan, seat) : null;
  };
  if (part === 'legs') {
    return (
      <div className="ticket-legs">
        <div className="ticket-legs-list">
          {draft.legs.map((l, index) => {
            const name = `${l.origin || '…'} → ${l.destination || '…'}`;
            // The leg before: the day and the clock it lands, its day else the one it leaves.
            const last = draft.legs[index - 1];
            const before = last && {
              landsOn: last.arrivalDay ?? last.departureDay,
              landsAt: last.arrivalTime.trim(),
            };
            const key = String(index);
            return (
              <fieldset
                key={index}
                className="ticket-leg"
                aria-label={`${t(flight ? 'ticket.flight' : 'ticket.leg', { number: index + 1 })} · ${name}`}
              >
                {/* Its heading across, pressed to fold it to that and open it again; what it is
                  folded to beside its name, the arrow and its removal at the right. */}
                <div className="ticket-leg-head">
                  <button
                    type="button"
                    className="ticket-leg-toggle"
                    aria-expanded={!folded.includes(index)}
                    onClick={() => {
                      setFolded((was) =>
                        was.includes(index)
                          ? was.filter((at) => at !== index)
                          : [...was, index],
                      );
                    }}
                  >
                    <span className="ticket-leg-title">
                      {t(flight ? 'ticket.flight' : 'ticket.leg', { number: index + 1 })} ·{' '}
                      {name}
                      {folded.includes(index) && (
                        <span className="quiet">
                          {[
                            l.number,
                            [l.departureTime, l.arrivalTime].filter(Boolean).join(' – '),
                          ]
                            .filter(Boolean)
                            .map((part) => ` · ${part}`)
                            .join('')}
                        </span>
                      )}
                    </span>
                    <ChevronIcon open={!folded.includes(index)} />
                  </button>
                  {draft.legs.length > 1 && (
                    <button
                      type="button"
                      className="icon-button icon-button-small icon-button-danger"
                      aria-label={t('ticket.removeLeg', { name })}
                      title={t('ticket.removeLeg', { name })}
                      onClick={() => {
                        onChange({
                          ...draft,
                          legs: draft.legs.filter((_, at) => at !== index),
                        });
                      }}
                    >
                      <BinIcon />
                    </button>
                  )}
                </div>
                <div className="ticket-leg-body" hidden={folded.includes(index)}>
                  <div className="form-row ticket-numbers">
                    {field(
                      `${key}-number`,
                      t(`ticket.numberBy.${mode ?? 'none'}`),
                      l.number,
                      (v) => {
                        // The airline a flight number says, until one is said.
                        const said =
                          flight && l.airline.trim() === ''
                            ? airlineOfFlight(v, airlines)
                            : null;
                        leg(index, {
                          number: v,
                          ...(said ? { airline: airlineLabel(said) } : {}),
                        });
                      },
                      '',
                      flight ? (
                        <Lookup
                          day={l.departureDay}
                          number={l.number}
                          onLook={(day) => {
                            fill(index, l, day);
                          }}
                        />
                      ) : null,
                      null,
                      'code',
                    )}
                    {flight && (
                      <>
                        <Suggest
                          label={t('ticket.airline')}
                          value={l.airline}
                          placeholder={t('ticket.airlineHint')}
                          suggestions={findAirlines(l.airline, airlines).map((a) => ({
                            value: airlineLabel(a),
                            label: airlineLabel(a),
                          }))}
                          onChange={(v) => {
                            leg(index, { airline: v });
                          }}
                        />
                        <Suggest
                          label={by('vehicleBy')}
                          value={l.vehicle}
                          placeholder={t('ticket.aircraftHint')}
                          suggestions={findAircraft(l.vehicle, aircraft).map((a) => ({
                            value: aircraftLabel(a),
                            label: aircraftLabel(a),
                          }))}
                          onChange={(v) => {
                            leg(index, { vehicle: v });
                          }}
                        />
                        {field(
                          `${key}-registration`,
                          by('registrationBy'),
                          l.registration,
                          (v) => {
                            leg(index, { registration: v });
                            if (l.vehicle.trim() === '') lookUp(index, v);
                          },
                        )}
                      </>
                    )}
                    {suggested && (
                      <>
                        <FoundSuggest
                          label={by('operatorBy')}
                          value={l.operator}
                          lists={suggested.operator}
                          onChange={(v) => {
                            leg(index, { operator: v });
                          }}
                          onPick={(one) => {
                            leg(index, { operator: one.name });
                          }}
                        />
                        {suggested.vehicle === undefined ? (
                          field(`${key}-vehicle`, by('vehicleBy'), l.vehicle, (v) => {
                            leg(index, { vehicle: v });
                          })
                        ) : (
                          // A ship chosen by its name says its IMO number and who runs it.
                          <FoundSuggest
                            label={by('vehicleBy')}
                            value={l.vehicle}
                            lists={suggested.vehicle}
                            // A car or a train type under its maker; a ship by its own name.
                            grouped={!ferry}
                            onChange={(v) => {
                              leg(index, { vehicle: v });
                            }}
                            onPick={(one) => {
                              leg(index, ferry ? aboard(l, one) : { vehicle: one.name });
                            }}
                          />
                        )}
                        {!train &&
                          field(
                            `${key}-registration`,
                            by('registrationBy'),
                            l.registration,
                            (v) => {
                              leg(index, { registration: v });
                              if (ferry && IMO.test(v.trim()))
                                setImo({ index, code: v.trim() });
                            },
                          )}
                      </>
                    )}
                    {classes.length > 0 && (
                      <Suggest
                        label={t('ticket.travelClass')}
                        value={l.travelClass}
                        openEmpty
                        suggestions={classes.map((one) => {
                          const named = t(`ticket.classes.${kind}.${one}`);
                          return { value: named, label: named };
                        })}
                        onChange={(v) => {
                          leg(index, { travelClass: v });
                        }}
                      />
                    )}
                  </div>
                  {ferry && (
                    <div className="form-row ticket-numbers">
                      <Pick<Carried>
                        label={t('ticket.carried')}
                        placeholder={t('ticket.carriedNone')}
                        searchable={false}
                        options={carriedKinds.map((one) => ({
                          value: one,
                          label: t(`ticket.carriedKind.${one}`),
                        }))}
                        value={l.carried}
                        onChange={(one) => {
                          leg(index, { carried: one });
                        }}
                        onClear={() => {
                          leg(index, { carried: null, carriedPlate: '' });
                        }}
                      />
                      {l.carried !== null &&
                        field(`${key}-plate`, t('ticket.carriedPlate'), l.carriedPlate, (v) => {
                          leg(index, { carriedPlate: v });
                        })}
                    </div>
                  )}
                  {lookedUp[index] !== undefined && (
                    <p className="quiet ticket-lookup" role="status">
                      {lookedUp[index]}
                    </p>
                  )}
                  <div className="ticket-ends">
                    <div
                      role="group"
                      aria-labelledby={`${ids}-${key}-dep`}
                      className="ticket-end"
                    >
                      <h4 id={`${ids}-${key}-dep`} className="ticket-end-head">
                        {t(car ? 'ticket.setOff' : 'ticket.departure')}
                      </h4>
                      <div className="form-row ticket-place">
                        {flight ? (
                          <Suggest
                            label={t('ticket.airport')}
                            value={l.origin}
                            suggestions={findAirports(l.origin, airports).map((a) => ({
                              value: a.code,
                              label: airportLabel(a),
                            }))}
                            onChange={(v) => {
                              leg(index, { origin: v });
                            }}
                          />
                        ) : (
                          place(
                            l.origin,
                            (v) => {
                              leg(index, { origin: v });
                            },
                            towns.from,
                            index === 0 ? 'from' : null,
                          )
                        )}
                        {terminals &&
                          field(
                            `${key}-terminal-from`,
                            t('ticket.terminal'),
                            l.terminalFrom,
                            (v) => {
                              leg(index, { terminalFrom: v });
                            },
                          )}
                      </div>
                      <div className="booking-when">
                        <DatePick
                          label={t('ticket.date')}
                          value={l.departureDay}
                          {...(before?.landsOn ? { from: before.landsOn } : {})}
                          onChange={(day) => {
                            leg(index, { departureDay: day });
                          }}
                        />
                        {field(
                          `${key}-departs`,
                          t('ticket.time'),
                          l.departureTime,
                          (v) => {
                            leg(index, { departureTime: v });
                          },
                          before?.landsAt &&
                            before.landsOn === (l.departureDay ?? before.landsOn)
                            ? t('ticket.after', { time: before.landsAt })
                            : t('ticket.clockHint'),
                          null,
                          null,
                          'clock',
                        )}
                      </div>
                      <div className="form-row ticket-numbers">
                        {field(
                          `${key}-actual-departs`,
                          t('ticket.actual'),
                          l.actualDeparture,
                          (v) => {
                            leg(index, { actualDeparture: v });
                          },
                          t('ticket.clockHint'),
                          null,
                          null,
                          'clock',
                        )}
                        {!car &&
                          field(`${key}-gate`, by('gateBy'), l.gate, (v) => {
                            leg(index, { gate: v });
                          })}
                      </div>
                      {flight &&
                        apron(`${key}-boarding`, t('ticket.boarding'), l.boarding, (way) => {
                          leg(index, { boarding: way });
                        })}
                    </div>
                    <div
                      role="group"
                      aria-labelledby={`${ids}-${key}-arr`}
                      className="ticket-end"
                    >
                      <h4 id={`${ids}-${key}-arr`} className="ticket-end-head">
                        {t(car ? 'ticket.arrive' : 'ticket.arrival')}
                      </h4>
                      <div className="form-row ticket-place">
                        {flight ? (
                          <Suggest
                            label={t('ticket.airport')}
                            value={l.destination}
                            suggestions={findAirports(l.destination, airports).map((a) => ({
                              value: a.code,
                              label: airportLabel(a),
                            }))}
                            onChange={(v) => {
                              leg(index, { destination: v });
                            }}
                          />
                        ) : (
                          place(
                            l.destination,
                            (v) => {
                              leg(index, { destination: v });
                            },
                            towns.to,
                            index === draft.legs.length - 1 ? 'to' : null,
                          )
                        )}
                        {terminals &&
                          field(
                            `${key}-terminal-to`,
                            t('ticket.terminal'),
                            l.terminalTo,
                            (v) => {
                              leg(index, { terminalTo: v });
                            },
                          )}
                      </div>
                      <div className="booking-when">
                        <DatePick
                          label={t('ticket.date')}
                          value={l.arrivalDay}
                          {...(l.departureDay ? { from: l.departureDay } : {})}
                          onChange={(day) => {
                            leg(index, { arrivalDay: day });
                          }}
                        />
                        {field(
                          `${key}-arrives`,
                          t('ticket.time'),
                          l.arrivalTime,
                          (v) => {
                            leg(index, { arrivalTime: v });
                          },
                          t('ticket.clockHint'),
                          null,
                          null,
                          'clock',
                        )}
                      </div>
                      <div className="form-row ticket-numbers">
                        {field(
                          `${key}-actual-arrives`,
                          t('ticket.actual'),
                          l.actualArrival,
                          (v) => {
                            leg(index, { actualArrival: v });
                          },
                          t('ticket.clockHint'),
                          null,
                          null,
                          'clock',
                        )}
                        {!car &&
                          field(`${key}-arrival-gate`, by('gateBy'), l.arrivalGate, (v) => {
                            leg(index, { arrivalGate: v });
                          })}
                      </div>
                      {flight &&
                        apron(`${key}-deplaning`, t('ticket.deplaning'), l.deplaning, (way) => {
                          leg(index, { deplaning: way });
                        })}
                    </div>
                  </div>
                </div>
              </fieldset>
            );
          })}
        </div>
      </div>
    );
  }
  if (part === 'rules') {
    const rules = draft.rules;
    const rule = (change: Partial<TicketRules>) => {
      onChange({ ...draft, rules: { ...rules, ...change } });
    };
    const count = (
      label: string,
      value: number | null,
      key: keyof TicketRules,
      max: number,
    ) => (
      <NumberField
        label={label}
        value={value}
        min={0}
        max={max}
        onChange={(n) => {
          rule({ [key]: n });
        }}
      />
    );
    return (
      <div className="ticket-rules">
        <fieldset className="ticket-frame">
          <legend className="ticket-frame-head">{t('ticket.cabin')}</legend>
          <div className="form-row ticket-numbers">
            {count(t('ticket.pieces'), rules.cabinPieces, 'cabinPieces', 9)}
            {count(t('ticket.kg'), rules.cabinKg, 'cabinKg', 99)}
            {field(
              'size',
              t('ticket.size'),
              rules.cabinSize ?? '',
              (v) => {
                rule({ cabinSize: v.trim() === '' ? null : v });
              },
              '55x40x23',
            )}
          </div>
          <label className="check">
            <input
              type="checkbox"
              checked={rules.personalItem}
              onChange={(event) => {
                rule({ personalItem: event.target.checked });
              }}
            />
            {t('ticket.personalItem')}
          </label>
        </fieldset>
        {/* Checked bags, once there is one: added from the bags' menu. */}
        {rules.checkedBags.length > 0 && (
          <fieldset className="ticket-frame">
            <legend className="ticket-frame-head">{t('ticket.checked')}</legend>
            {bagList(rules.checkedBags, (checkedBags) => {
              rule({ checkedBags });
            })}
          </fieldset>
        )}
        <fieldset className="ticket-frame">
          <legend className="ticket-frame-head">{t('ticket.checkIn')}</legend>
          <div className="form-row ticket-check-in">
            {count(t('ticket.onlineOpens'), rules.onlineOpensH, 'onlineOpensH', 999)}
            {count(t('ticket.onlineCloses'), rules.onlineClosesM, 'onlineClosesM', 9999)}
            {count(t('ticket.deskOpens'), rules.deskOpensH, 'deskOpensH', 99)}
            {count(t('ticket.deskCloses'), rules.deskClosesM, 'deskClosesM', 9999)}
          </div>
        </fieldset>
        <div className="field">
          <label className="field-label" htmlFor={`${ids}-notes`}>
            {t('ticket.notes')}
          </label>
          <textarea
            id={`${ids}-notes`}
            className="text-input"
            rows={3}
            value={rules.notes}
            onChange={(event) => {
              rule({ notes: event.target.value });
            }}
          />
        </div>
      </div>
    );
  }
  const shown = draft.passengers[person];
  const share: Money | null =
    price === null || draft.passengers.length === 0
      ? null
      : { currency, minor: Math.round(price / draft.passengers.length) };
  return (
    <div className="ticket-people">
      <div className="ticket-person-tabs" role="group" aria-label={t('ticket.people')}>
        {draft.passengers.map((p, index) => {
          const name =
            p.name.trim() === '' ? t('ticket.person', { number: index + 1 }) : p.name;
          return (
            <span key={index} className="ticket-person-chip">
              <button
                type="button"
                className="ticket-person-tab"
                aria-pressed={index === person}
                onClick={() => {
                  setPerson(index);
                }}
              >
                {name}
              </button>
              {/* Taken off the ticket by the × at its right. */}
              <button
                type="button"
                className="ticket-person-remove"
                aria-label={t('ticket.removePerson', { name })}
                title={t('ticket.removePerson', { name })}
                onClick={() => {
                  onChange({
                    ...draft,
                    passengers: draft.passengers.filter((_, at) => at !== index),
                  });
                  setPerson(0);
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        <button
          type="button"
          className="button button-small"
          onClick={() => {
            onChange({
              ...draft,
              passengers: [
                ...draft.passengers,
                {
                  name: '',
                  traveller: null,
                  price: null,
                  number: '',
                  seats: [],
                  coaches: [],
                  kinds: [],
                  bags: null,
                },
              ],
            });
            setPerson(draft.passengers.length);
          }}
        >
          {t('ticket.addPerson')}
        </button>
      </div>
      {shown && (
        <div key={person} className="ticket-person">
          {/* Chosen from the trip's people, or a name typed that is none of them. */}
          <Suggest
            label={t('ticket.name')}
            value={shown.name}
            placeholder={t('ticket.nameHint')}
            openEmpty
            suggestions={peopleFor(shown.name, members, travellers, t)}
            onChange={(v) => {
              passenger(person, {
                name: v,
                traveller: travellers.find((one) => one.name === v)?.id ?? null,
              });
            }}
          />
          <div className="form-row">
            <AmountField
              label={t('ticket.price')}
              value={shown.price}
              currency={currency}
              onChange={(minor) => {
                passenger(person, { price: minor });
              }}
            />
            {field(
              `p${String(person)}-number`,
              t('ticket.ticketNumber'),
              shown.number,
              (v) => {
                passenger(person, { number: v });
              },
              '',
              null,
              null,
              'code',
            )}
          </div>
          {share && shown.price === null && (
            <p className="quiet">
              {t('ticket.share', { amount: (share.minor / 100).toFixed(2) })}
            </p>
          )}
          {/* Their bags: the ticket's, or their own — a bag more, another fare. */}
          <div className="field">
            <span className="field-label" id={`${ids}-p${String(person)}-bags`}>
              {t('ticket.bags')}
            </span>
            <div className="bags-row">
              <div
                className="segmented"
                role="group"
                aria-labelledby={`${ids}-p${String(person)}-bags`}
              >
                {([false, true] as const).map((own) => (
                  <button
                    key={String(own)}
                    type="button"
                    aria-pressed={(shown.bags !== null) === own}
                    onClick={() => {
                      passenger(person, {
                        bags: own
                          ? { cabinKg: draft.rules.cabinKg, checked: draft.rules.checkedBags }
                          : null,
                      });
                    }}
                  >
                    {t(own ? 'ticket.ownBags' : 'ticket.ticketBags')}
                  </button>
                ))}
              </div>
              {/* Their own: a checked bag more, by the plus beside. */}
              {shown.bags !== null && (
                <AddBag
                  bags={shown.bags}
                  onChange={(bags) => {
                    passenger(person, { bags });
                  }}
                />
              )}
            </div>
          </div>
          {shown.bags !== null && (
            <OwnBags
              bags={shown.bags}
              list={bagList}
              onChange={(bags) => {
                passenger(person, { bags });
              }}
            />
          )}
          {/* A seat on each leg, by train its coach beside it; a hired car has none. */}
          {!car && (
            <div className="form-row ticket-numbers">
              {draft.legs.map((l, index) => {
                const named = { leg: `${l.origin} → ${l.destination}` };
                const at = `p${String(person)}-${String(index)}`;
                const place = placeFor(l, shown.seats[index] ?? '');
                const each = (typed: string[], v: string) =>
                  draft.legs.map((_, i) => (i === index ? v : (typed[i] ?? '')));
                return (
                  <Fragment key={index}>
                    {train &&
                      field(
                        `${at}-coach`,
                        t('ticket.coach', named),
                        shown.coaches[index] ?? '',
                        (v) => {
                          passenger(person, { coaches: each(shown.coaches, v) });
                        },
                      )}
                    <div className="ticket-seat">
                      {field(
                        `${at}-seat`,
                        by('seatBy', named),
                        shown.seats[index] ?? '',
                        (v) => {
                          passenger(person, { seats: each(shown.seats, v) });
                        },
                        '',
                        null,
                        // Where it is beside it: picked, or as the aircraft's seat map says;
                        // by air, its seat map at the end, under the seats when pressed.
                        <>
                          <SeatKindPick
                            seat={shown.seats[index] ?? ''}
                            value={shown.kinds[index] ?? null}
                            known={place?.kind ?? null}
                            onChange={(kind) => {
                              const kinds = draft.legs.map((_, i) => shown.kinds[i] ?? null);
                              kinds[index] = kind;
                              passenger(person, { kinds });
                            }}
                          />
                          {flight && (
                            <button
                              type="button"
                              className="icon-button seat-map-toggle"
                              aria-expanded={seatMapOf === index}
                              aria-label={t(
                                typeCodeOf(l.vehicle, aircraft) === null
                                  ? 'seatMap.needsAircraft'
                                  : 'seatMap.openFor',
                                { leg: `${l.origin} → ${l.destination}` },
                              )}
                              title={t(
                                typeCodeOf(l.vehicle, aircraft) === null
                                  ? 'seatMap.needsAircraft'
                                  : 'seatMap.openFor',
                                { leg: `${l.origin} → ${l.destination}` },
                              )}
                              disabled={typeCodeOf(l.vehicle, aircraft) === null}
                              onClick={() => {
                                setSeatMapOf(seatMapOf === index ? null : index);
                              }}
                            >
                              <SeatMapIcon />
                            </button>
                          )}
                        </>,
                      )}
                      {place && (
                        <span className="quiet ticket-seat-place">
                          {t(`seatMap.side.${place.side}`)}
                        </span>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          )}
          {flight && (
            <div className="ticket-seatmaps">
              {seatMapLeg && seatMapType !== null && (
                <SeatMap
                  icao={seatMapType}
                  airline={airlineCode(seatMapLeg.airline, airlines)}
                  marked={draft.passengers.flatMap((p, at) => {
                    const seat = p.seats[mapIndex] ?? '';
                    return seat.trim() === '' ? [] : [{ seat, own: at === person }];
                  })}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
